import React, { useEffect, useRef } from "react";
import { connect } from "react-redux";
import { Button, NumberBox, Popup } from "devextreme-react";
import axios from "axios";
import notify from "devextreme/ui/notify";
import { MESSAGES } from "../../../../../../common/Messages";
import data from "../../../../../../assets/apiRoutes.json";
import { TransactionStage } from "../../../../../../enum/Enum";
// Reuses the shared operation-screen styling so the yellow help banner matches the other WM
// operation screens (e.g. Calculate Deferred Handling Revenue) rather than drifting from them.
import "../../../../charges/common/operationScreen.css";

// WM Enter Weights - the port of TfrmWMD0111 ("Enter Weights From Scale (WMD0111)", whose own header
// comment reads "WM Calculate Net Weight From the Scale Weights").
//
// The whole calculation is one line (uWMD0111.pas:117-121):
//     edtNetWeight.Value := edtGrossWeight.Value - edtTareWeight.Value;
// wired to OnChange of BOTH edits (:188-191), so Net recomputes on every keystroke rather than on
// blur. edtGrossWeightExit / edtTareWeightExit exist in the unit but the DFM assigns no OnExit, so
// they are dead code - do not be misled into a blur-based implementation by them.
//
// All three fields open at 0.0000 every single time: FormCreate seeds nothing (:129-132). There is
// NO tare lookup anywhere in the legacy unit - no per-package, pallet or packaging tare - and no
// validation whatsoever on any of the three weights, so a zero or negative Net is accepted by
// design. The sibling UpdateSubDetailTotalWeight.js DOES pre-seed from the selected row; that is
// correct for WMD0090 and wrong here.
//
// What actually persists is only the derived Net, and it persists as a QUANTITY. The Delphi caller
// writes exactly two columns on the current WMTRANSACTIONSUBDETAIL row (uWMD0002.pas:14739-14740):
//     QUANTITYORDER  := f.edtNetWeight.Value;
//     QUANTITYACTUAL := f.edtNetWeight.Value;
// NETWEIGHT, GROSSWEIGHT and WEIGHTSCHANGED are deliberately untouched - those belong to WMD0090,
// which cascades into the IMSUBLOT master's average weights. The Gross and Tare the operator typed
// are discarded and never stored. See the payload note in saveNetWeight for why that matters here.
//
// TEAM DECISIONS that diverge from Delphi:
//   - The serial scale is NOT implemented. Delphi read Gross over RS-232 and had a Configure tab for
//     the COM port; a browser cannot open COM1 and the API is a shared multi-tenant host, so there is
//     no faithful web equivalent. Gross is typed in. The Configure tab therefore has no counterpart.
//   - "OK & Print" is present but permanently DISABLED, labelled "(Under Construction)", matching
//     MESSAGES.FUNCTIONALITY_UNDER_CONSTRUCTION. Delphi printed the WMR0037 receiving labels for
//     the sub-detail (uWMD0002.pas:14742-14769); that report is not
//     ported to .NET yet, so there is nothing to print. The React side of the call is designed and
//     recorded in the plan's handover section - roughly 30 lines of work once the report exists.
//   - Renamed "Enter Weights", in the popup title and the sublot operations menu alike, because
//     "From Scale" would name a capability the screen no longer has.
//   - GROSS takes focus and is tabbable. Delphi focused Tare and marked Gross TabStop=False, because
//     on a wired workstation the scale filled Gross for you; with the scale gone Gross is always
//     typed, so it leads. Net stays read-only and out of the tab order, as in Delphi, because it is
//     always derived.
//
// STAGE RULE. Delphi treats QUANTITYACTUAL as a Ready/Completed concept - every place that maintains
// it is gated on stage 'R' or 'C':
//     uWMM0002.PAS:7146-7153  QUANTITYORDERChange copies Ordered -> Actual only when the item is a
//                             sublot, the row is inserting, and the stage is 'R' or 'C'
//     uWMD0002.pas:9105-9108  edtSublotActualEnter defaults Actual from Ordered only at 'R'/'C'
//     uWMM0002.PAS:4306-4312  a new sub-detail at 'P'/'F'/'N' sets QUANTITYORDER only
// So before Ready only the Ordered (pending) quantity moves. WMD0111's own caller assigns both
// unconditionally, but at those stages Actual is not a maintained quantity - which is why the
// operator only ever sees the pending figure change. We send what the stage actually maintains, and
// verify only what we sent; the endpoint gates its cascades on which keys are present, so a
// single-key payload is fully supported (TransactionSubLotService.cs:858-859, :939-942).

function EnterWeights(props) {
    var accessToken = props.oidc.user.access_token;
    const grossRef = useRef(null);
    // The parent renders this popup conditionally, so closePopup() unmounts us. Printing can
    // still be in flight at that point, and a bare setBusy(false) in the finally would fire on an
    // unmounted component. Track mounted-ness rather than reordering the teardown.
    const mounted = useRef(true);
    useEffect(() => () => { mounted.current = false; }, []);

    const [formFields, setformFields] = React.useState({ GrossWeight: 0, TareWeight: 0 });
    const [busy, setBusy] = React.useState(false);

    // Derived, never held in state - this is the entirety of CalculateNetWeight.
    const netWeight = Number(formFields.GrossWeight ?? 0) - Number(formFields.TareWeight ?? 0);

    // Actual is only maintained from the Ready stage onwards - see the STAGE RULE note above.
    const writesActual = props.stage === TransactionStage.Ready
        || props.stage === TransactionStage.Completed;

    const currentOrdered = Number(props.showPopup.subDetail?.quantityorder ?? 0);
    const currentActual = Number(props.showPopup.subDetail?.quantityactual ?? 0);
    // Two decimals, matching how the sublot grid renders these columns (format="#,##0.00"). The
    // weights above stay at 4 dp - these are the quantities the Net lands in, not the weights. The
    // stored value keeps its full scale either way; only the display rounds, exactly as the grid does.
    const asQuantity = (n) => Number(n ?? 0).toLocaleString(undefined, {
        minimumFractionDigits: 2, maximumFractionDigits: 2,
    });

    const handleFieldChange = (name, value) => {
        setformFields((prevFormFields) => ({
            ...prevFormFields,
            [name]: value,
        }));
    };

    const closePopup = () => props.setShowPopup((prev) => ({ ...prev, visible: false }));

    /**
     * Writes the derived Net into quantityorder + quantityactual and returns the refreshed row, or
     * null when the write did not land. Shared by OK and OK & Print so the two cannot drift.
     *
     * Exactly three keys go on the wire, and both the inclusion and the omission are load-bearing:
     *  - sublotCode is MANDATORY. ConvertToDTO does not project it, so without it
     *    GetTransactionSubLotId returns null, the service takes its CreateTransactionSubLot branch,
     *    inserts an IMSUBLOT row with a NULL code, and - because the new Guid is never assigned back
     *    to dto.RSublot - nulls this row's sublot FK. The sublot grid's rowSubLotUpdating back-fills
     *    it on every cell edit for the same reason.
     *  - netweight / grossweight must NOT be sent. On the existing-sublot path the service
     *    PopulateObjects the same values string onto a sublot DTO and writes Netweight / Grossweight
     *    onto the IMSUBLOT MASTER, i.e. it would silently do WMD0090's job.
     */
    const saveNetWeight = async (net) => {
        const row = props.showPopup.subDetail;

        // Ordered always; Actual only where the stage maintains it. Sending Actual before Ready made
        // the verification below fail on a save that had in fact done what Delphi does.
        const values = { quantityorder: net, sublotCode: row?.sublotCode };
        if (writesActual) values.quantityactual = net;

        const body = new URLSearchParams();
        body.append("key", row?.rowid);
        body.append("values", JSON.stringify(values));

        const res = await axios.put(
            `${window._env_.REACT_APP_APIS}/${data.TransactionSubLot.UpdateSubLotDetailsUrl}`,
            body,
            {
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                    access_token: accessToken,
                    Authorization: `Bearer ${accessToken}`,
                },
            }
        );

        // The service swallows its own exceptions and still returns a re-read row, so a 200 is not
        // proof of a write. Comparing the quantities catches a failed SaveChanges; it cannot catch a
        // failed cascade, which returns matching quantities with wrong parent totals.
        //
        // Compare with a tolerance, never ===: `net` is a JS subtraction and the column is a SQL
        // decimal, so e.g. 5.3 - 1.1 is 4.199999999999999 here and 4.2000 there, and strict equality
        // would report a perfectly good save as a failure. Half of the column's 4-dp scale is the
        // widest difference that can be pure representation error.
        const matches = (value) => Math.abs(Number(value) - net) < 0.00005;

        const saved = res.data;
        const landed = saved
            && matches(saved.quantityorder)
            // Only what we actually sent. Before Ready the server rightly leaves Actual alone.
            && (!writesActual || matches(saved.quantityactual));

        return landed ? saved : null;
    };

    /**
     * Reproduces what the sublot CustomStore's own onUpdated does, because the direct PUT bypasses
     * the store. The location grid is refreshed unconditionally: onUpdated only does so when
     * quantityactual changed, and we always change it.
     */
    const refreshAfterSave = (saved) => {
        if (props.setSelectedSubLot) props.setSelectedSubLot(saved);
        if (props.refreshSublotGrid) props.refreshSublotGrid();
        setTimeout(() => { if (props.RefereshGrid) props.RefereshGrid(); }, 100);
        if (props.refreshLocationGrid) props.refreshLocationGrid();
        // Delphi recalculates the transaction totals on return from the modal
        // (uWMD0002.pas:14281, AB_WMD0002_RunCalculateTransactionTotals).
        if (props.changeTotals) props.changeTotals();
    };

    // Name only the columns this stage actually writes - claiming Actual moved when it did not is
    // exactly the confusion this screen already caused once.
    const updatedMessage = writesActual
        ? MESSAGES.WEIGHT_QUANTITIES_UPDATED
        : MESSAGES.WEIGHT_ORDERED_QUANTITY_UPDATED;
    const updateFailedMessage = writesActual
        ? MESSAGES.WEIGHT_QUANTITIES_UPDATE_FAILED
        : MESSAGES.WEIGHT_ORDERED_QUANTITY_UPDATE_FAILED;

    const applyNetWeight = async () => {
        setBusy(true);
        try {
            props.setLoadPanelVisible(true);
            const saved = await saveNetWeight(netWeight);
            if (!saved) {
                notify({ message: updateFailedMessage, width: 400 }, "error", 3000);
                return;
            }

            refreshAfterSave(saved);
            notify({ message: updatedMessage, width: 400 }, "success", 3000);
            closePopup();
        } catch (error) {
            // Leave the popup open with the weights intact so the operator does not re-key them.
            // Never surface the server's raw "Error: " + ex.Message text.
            console.error("Enter Weights failed:", error);
            notify({ message: updateFailedMessage, width: 400 }, "error", 3000);
        } finally {
            props.setLoadPanelVisible(false);
            if (mounted.current) setBusy(false);
        }
    };

    return (
        <>
            {props.showPopup.visible === true ? (
                <Popup
                    visible={props.showPopup.visible}
                    onHiding={closePopup}
                    onShown={() => { if (grossRef.current) grossRef.current.instance().focus(); }}
                    dragEnabled={false}
                    hideOnOutsideClick={true}
                    showCloseButton={true}
                    showTitle={true}
                    title={props.title}
                    container="#root"
                    width={480}
                    height="auto"
                >
                    {/* Laid out to match the legacy dialog, which is a compact 406x353: help text
                        across the top, the three weights stacked below it, then the buttons in a
                        single row. height="auto" keeps the popup off the dead space a fixed vh
                        leaves under the fields. */}
                    <div className="op-help">{MESSAGES.ENTER_WEIGHTS_HELP}</div>

                    {/* Only col-fieldset (the border) - NOT the sibling's "list group-col", which is
                        display:inline-block/width:240px and flex:0 1 25%. Those pin the group to
                        240px and are what left the dead space to its right. */}
                    <fieldset className="col-fieldset" style={{ padding: "0 12px 12px 12px" }}>
                        <legend className="list-title">Weights</legend>

                        <div style={{ marginBottom: "1.1rem" }}>
                            <NumberBox
                                ref={grossRef}
                                label="Gross Weight"
                                labelMode="static"
                                stylingMode="outlined"
                                format="#,##0.0000"
                                value={formFields.GrossWeight}
                                valueChangeEvent="keyup input change"
                                onValueChanged={(e) => handleFieldChange("GrossWeight", e.value)}
                            />
                        </div>

                        <div style={{ marginBottom: "1.1rem" }}>
                            <NumberBox
                                label="Tare Weight"
                                labelMode="static"
                                stylingMode="outlined"
                                format="#,##0.0000"
                                value={formFields.TareWeight}
                                valueChangeEvent="keyup input change"
                                onValueChanged={(e) => handleFieldChange("TareWeight", e.value)}
                            />
                        </div>

                        <div>
                            <NumberBox
                                label="Net Weight"
                                labelMode="static"
                                stylingMode="outlined"
                                format="#,##0.0000"
                                value={netWeight}
                                readOnly={true}
                                tabIndex={-1}
                            />
                        </div>

                        {/* Live preview of what OK will do, so the operator never has to press it to
                            find out - and so the stage rule is visible rather than surprising. */}
                        <div style={{ marginTop: "1rem", paddingTop: "0.75rem", borderTop: "1px solid #ddd", fontSize: "0.85rem" }}>
                            <div style={{ fontWeight: 600, marginBottom: "0.35rem" }}>{MESSAGES.WEIGHT_PREVIEW_TITLE}</div>
                            <div style={{ display: "flex", justifyContent: "space-between", fontVariantNumeric: "tabular-nums" }}>
                                <span>{MESSAGES.WEIGHT_PREVIEW_ORDERED}</span>
                                <span>{asQuantity(currentOrdered)} &rarr; <strong>{asQuantity(netWeight)}</strong></span>
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-between", fontVariantNumeric: "tabular-nums", opacity: writesActual ? 1 : 0.65 }}>
                                <span>{MESSAGES.WEIGHT_PREVIEW_ACTUAL}</span>
                                <span>
                                    {asQuantity(currentActual)} &rarr;{" "}
                                    {writesActual
                                        ? <strong>{asQuantity(netWeight)}</strong>
                                        : <em>{MESSAGES.WEIGHT_PREVIEW_ACTUAL_UNCHANGED}</em>}
                                </span>
                            </div>
                        </div>
                    </fieldset>

                    {/* Delphi puts every button on one row at the foot of the dialog. */}
                    <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", marginTop: "0.8rem" }}>
                        <Button type="default" text="Ok" onClick={applyNetWeight} disabled={busy} />
                        {/* Kept for parity with Delphi's OK && Print, but the WMR0037 receiving-label
                            report is not ported yet so there is nothing to print. The tooltip lives on
                            a wrapping span because DevExtreme's dx-state-disabled sets
                            pointer-events:none, so `hint` alone never shows - same treatment as the
                            disabled print buttons on CalculateDeferredRevenue.js:393-404. */}
                        <span
                            title={MESSAGES.FUNCTIONALITY_UNDER_CONSTRUCTION}
                            style={{ display: "inline-block", cursor: "not-allowed" }}
                        >
                            <Button
                                text="OK & Print (Under Construction)"
                                disabled={true}
                                hint={MESSAGES.FUNCTIONALITY_UNDER_CONSTRUCTION}
                            />
                        </span>
                    </div>
                </Popup>
            ) : (null)}
        </>
    );
}

function mapStateToProps(state) {
    return {
        ReceiptReducer: state.ReceiptReducer,
        oidc: state.oidc,
    };
}

export default connect(mapStateToProps)(EnterWeights);
